<template>
  <BittsCollapse
    :is-open="isOpen"
    :keep-mounted="true"
    :aria-label="partner.partner_name"
    class="irp-partner-row"
    :class="{ 'irp-partner-row--open': isOpen }"
    @update:is-open="setIsOpen"
  >
    <template #title>
      <div class="irp-partner-row__header">
        <div class="irp-partner-row__identity">
          <BittsAvatar
            :org="partnerOrg"
            size="small"
            shape="square"
            class="shrink-0"
          />
          <div
            class="irp-partner-row__identity-text ph-no-capture dd-privacy-mask"
          >
            <span class="irp-partner-row__name-line">
              <span
                class="irp-partner-row__name"
                data-testid="irp-partner-row-name"
              >
                {{ partner.partner_name }}
              </span>
              <MatchIssueFlag
                :match-issues="matchIssues"
                class="shrink-0 text-brand-red"
                data-testid="irp-partner-row-match-issue-flag"
              />
            </span>
            <span v-if="subtitle" class="irp-partner-row__subtitle">
              {{ subtitle }}
            </span>
          </div>
        </div>

        <div
          v-if="hasColumn('score')"
          class="irp-partner-row__column irp-partner-row__column--score"
          data-testid="irp-partner-row-score"
        >
          <span class="irp-partner-row__column-label">Partner Score</span>
          <PartnerScoreTag
            v-if="partnerOrg"
            :partner="partnerOrg"
            :with-breakdown="true"
          />
          <span v-else class="irp-partner-row__column-value">—</span>
        </div>

        <div
          v-if="hasColumn('owner')"
          class="irp-partner-row__column irp-partner-row__column--owner"
        >
          <span class="irp-partner-row__column-label">Account Owner</span>
          <span
            class="irp-partner-row__column-value irp-partner-row__owner ph-no-capture dd-privacy-mask"
            data-testid="irp-partner-row-owner"
          >
            <FontAwesomeIcon
              v-if="ownerName"
              :icon="['fak', 'person']"
              class="irp-partner-row__owner-icon"
            />
            <span class="truncate">{{ ownerName || '—' }}</span>
          </span>
        </div>

        <div
          v-if="hasColumn('contacts')"
          class="irp-partner-row__column irp-partner-row__column--contacts"
        >
          <span class="irp-partner-row__column-label">Contacts</span>
          <span
            class="irp-partner-row__column-value"
            data-testid="irp-partner-row-contacts"
          >
            {{ contactsCount }}
          </span>
        </div>

        <div
          v-if="hasColumn('opportunities')"
          class="irp-partner-row__column irp-partner-row__column--opportunities"
        >
          <span class="irp-partner-row__column-label">Open Opportunities</span>
          <span
            class="irp-partner-row__column-value"
            data-testid="irp-partner-row-opportunities"
          >
            {{ opportunitiesCount }}
          </span>
        </div>

        <div
          v-if="hasColumn('populations')"
          class="irp-partner-row__column irp-partner-row__column--populations"
        >
          <span class="irp-partner-row__column-label">Populations</span>
          <div class="irp-partner-row__populations">
            <template v-if="firstPopulation">
              <BittsTag
                color="info"
                size="x-small"
                class="irp-partner-row__population-tag"
              >
                <IRPFieldGridText
                  :text="firstPopulation.name"
                  :is-mounted-to-body="true"
                />
              </BittsTag>
              <BittsPopover
                v-if="overflowPopulations.length"
                trigger="hover"
                placement="bottomLeft"
                :mount-to-body="true"
              >
                <span class="irp-partner-row__populations-overflow">
                  +{{ overflowPopulations.length }}
                </span>
                <template #content>
                  <div class="irp-partner-row__populations-list">
                    <BittsTag
                      v-for="population in overflowPopulations"
                      :key="population.id"
                      :text="population.name"
                      color="info"
                      size="x-small"
                    />
                  </div>
                </template>
              </BittsPopover>
            </template>
            <span v-else class="irp-partner-row__column-value">—</span>
          </div>
        </div>

        <div
          v-if="hasColumn('tags')"
          class="irp-partner-row__column irp-partner-row__column--tags"
          data-testid="irp-partner-row-tags"
        >
          <span class="irp-partner-row__column-label">Tags</span>
          <div class="irp-partner-row__tags">
            <template v-if="firstTag">
              <BittsTag
                :text="firstTag.label"
                :color="tagColor(firstTag)"
                size="x-small"
                class="irp-partner-row__tag"
              />
              <BittsPopover
                v-if="overflowTags.length"
                trigger="hover"
                placement="bottomLeft"
                :mount-to-body="true"
              >
                <span class="irp-partner-row__tags-overflow">
                  +{{ overflowTags.length }}
                </span>
                <template #content>
                  <div class="irp-partner-row__tags-list">
                    <BittsTag
                      v-for="tag in overflowTags"
                      :key="String(tag.id)"
                      :text="tag.label"
                      :color="tagColor(tag)"
                      size="x-small"
                    />
                  </div>
                </template>
              </BittsPopover>
            </template>
            <span v-else class="irp-partner-row__column-value">—</span>
          </div>
        </div>

        <RouterLink
          v-if="partnerDetailsRoute"
          :to="partnerDetailsRoute"
          class="irp-partner-row__profile-link"
          data-testid="irp-partner-row-profile-link"
          @click.stop="trackViewPartnerProfile"
        >
          View partner profile
        </RouterLink>
      </div>
    </template>

    <div class="irp-partner-row__body" @click.stop>
      <slot />
    </div>
  </BittsCollapse>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsCollapse,
  BittsPopover,
  BittsTag,
} from '@crossbeam/bitts';
import { IRP_CTAS } from '@crossbeam/itly';

import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import PartnerScoreTag from '@/components/partners/PartnerScoreTag.vue';
import MatchIssueFlag from '@/components/records/MatchIssueFlag.vue';

import {
  useIndividualRecordStore,
  useMatchIssuesStore,
  usePartnersStore,
  usePopulationsStore,
} from '@/stores';
import type { RecordPartnerAnyType } from '@/types/individual_record';
import type { PartnerTag } from '@/types/partners';

import { byPopulationPriority, openOpportunityCount, ownerNameOf } from './irp';
import type { IRPPartnerColumnType } from './irp';
import IRPFieldGridText from './IRPFieldGridText.vue';
import { useIRPTracking } from './useIRPTracking';

const {
  partner,
  columns,
  populationIds,
  subtitle = '',
  activeTagIds = [],
} = defineProps<{
  partner: RecordPartnerAnyType;
  columns: IRPPartnerColumnType[];
  populationIds?: number[];
  subtitle?: string;
  activeTagIds?: string[];
}>();

const partnersStore = usePartnersStore();
const populationsStore = usePopulationsStore();
const matchIssuesStore = useMatchIssuesStore();
const individualRecordStore = useIndividualRecordStore();

const isOpen = defineModel<boolean>('isOpen', { default: false });

const { trackIRPInteraction } = useIRPTracking();

const partnerOrg = computed(() =>
  partnersStore.getPartnerOrgByUuid(partner.partner_organization_uuid),
);

const matchIssues = computed(() => {
  const { sourceId, sourcePrimaryKey } = individualRecordStore;
  const partnerOrgId = partnerOrg.value?.id;
  if (!sourceId || !sourcePrimaryKey || !partnerOrgId) return [];
  return matchIssuesStore
    .getMatchIssuesBySourceIdAndRecordId(sourceId, sourcePrimaryKey)
    .filter(
      (issue) =>
        issue.reporting_organization.id === partnerOrgId ||
        issue.partner_organization.id === partnerOrgId,
    );
});

const partnerDetailsRoute = computed<RouteLocationRaw | null>(() => {
  const partnerOrgId = partnerOrg.value?.id;
  if (!partnerOrgId) return null;
  return { name: 'partner_details', params: { partner_org_id: partnerOrgId } };
});

function setIsOpen(value: boolean) {
  isOpen.value = value;
  trackIRPInteraction({
    cta: IRP_CTAS.PARTNER_ROW,
    cta_2: value ? 'expand' : 'collapse',
    partner_org_id: String(partner.partner_organization_id),
  });
}

function trackViewPartnerProfile() {
  trackIRPInteraction({
    cta: IRP_CTAS.VIEW_PARTNER_PROFILE,
    partner_org_id: String(partner.partner_organization_id),
  });
}

const resolvedPopulationIds = computed(
  () => populationIds ?? partner.partner_population_ids ?? [],
);

const populations = computed(() =>
  resolvedPopulationIds.value
    .map(populationsStore.getPartnerPopulationById)
    .filter((population) => !!population)
    .toSorted(byPopulationPriority),
);

const firstPopulation = computed(() => populations.value[0]);
const overflowPopulations = computed(() => populations.value.slice(1));

function isActiveTag(tag: PartnerTag) {
  return activeTagIds.includes(String(tag.id));
}

function tagColor(tag: PartnerTag) {
  return isActiveTag(tag) ? 'accent' : 'neutral';
}

const partnerTags = computed(
  () =>
    partnersStore.getPartnerOrgByUuid(partner.partner_organization_uuid, false)
      ?.tags ?? [],
);

const tags = computed(() =>
  [...partnerTags.value].sort(
    (a, b) =>
      Number(isActiveTag(b)) - Number(isActiveTag(a)) ||
      a.label.localeCompare(b.label),
  ),
);

const firstTag = computed(() => tags.value[0]);
const overflowTags = computed(() => tags.value.slice(1));

function hasColumn(column: IRPPartnerColumnType) {
  return columns.includes(column);
}

const ownerName = computed(() =>
  'account_owners' in partner
    ? ownerNameOf(partner.account_owners.items[0]?.fields ?? [])
    : '',
);

const contactsCount = computed(() =>
  'contacts' in partner ? partner.contacts.items.length : 0,
);

const opportunitiesCount = computed(() =>
  'opportunities' in partner
    ? openOpportunityCount(partner.opportunities.items)
    : 0,
);
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";
.irp-partner-row {
  @apply rounded-8 px-16 py-8;
}

.irp-partner-row--open {
  @apply bg-neutral-background-weak;
}

.irp-partner-row__header {
  @apply flex items-center justify-between gap-16 w-full;
}

.irp-partner-row__identity {
  @apply flex items-center gap-8 flex-[3] min-w-0;
}

.irp-partner-row__identity-text {
  @apply flex flex-col justify-center min-w-0 leading-4;
}

.irp-partner-row__name-line {
  @apply flex items-center gap-4 min-w-0;
}

.irp-partner-row__name {
  @apply truncate text-sm font-semibold text-neutral-text-strong;
}

.irp-partner-row__subtitle {
  @apply truncate text-sm text-neutral-text-weak;
}

.irp-partner-row__column {
  @apply flex flex-col gap-2 min-w-0;
}

.irp-partner-row__column--score {
  @apply flex-1 min-w-fit;
}

.irp-partner-row__column--owner {
  @apply flex-[2];
}

.irp-partner-row__column--contacts {
  @apply flex-1 min-w-fit;
}

.irp-partner-row__column--opportunities {
  @apply flex-[2] min-w-fit;
}

.irp-partner-row__column--populations,
.irp-partner-row__column--tags {
  @apply flex-[2] min-w-[130px];
}

.irp-partner-row__owner {
  @apply flex items-center gap-4 min-w-0;
}

.irp-partner-row__owner-icon {
  @apply shrink-0 text-neutral-accent;
}

.irp-partner-row__column-label {
  @apply truncate text-xs text-neutral-text-weak;
}

.irp-partner-row__column-value {
  @apply text-sm text-neutral-text-strong;
}

.irp-partner-row__populations {
  @apply flex items-center gap-4;
}

.irp-partner-row__population-tag {
  @apply min-w-0;
}

.irp-partner-row__population-tag :deep(.ant-tag) {
  @apply mr-0 max-w-full;
}

.irp-partner-row__populations-overflow {
  @apply shrink-0 cursor-default text-sm text-neutral-text-weak;
}

.irp-partner-row__populations-list {
  @apply flex flex-col items-start gap-4 p-8;
}

.irp-partner-row__tags {
  @apply flex items-center gap-4;
}

.irp-partner-row__tag {
  @apply min-w-0;
}

.irp-partner-row__tag :deep(.ant-tag) {
  @apply mr-0 truncate block;
}

.irp-partner-row__tags-overflow {
  @apply shrink-0 cursor-default text-sm text-neutral-text-weak;
}

.irp-partner-row__tags-list {
  @apply flex flex-col items-start gap-4 p-8;
}

.irp-partner-row__profile-link {
  @apply shrink-0 text-sm font-semibold text-neutral-text whitespace-nowrap;
}

.irp-partner-row__body {
  @apply mt-16 flex flex-col gap-16;
}

.irp-partner-row :deep(.bitts-collapse__header),
.irp-partner-row :deep(.bitts-collapse__prefix-wrapper) {
  @apply h-auto;
}
</style>
